export interface GuardGroup {
  id: string;
  label: string;
  hint: string;
  /** Appended to the prompt when active. */
  positive?: string;
  /** Appended to the negative prompt when the model supports one. */
  negative?: string;
  /** Used instead of `negative` when the model has no negative prompt. */
  negativeFallback?: string;
}

export const GUARD_GROUPS: GuardGroup[] = [
  {
    id: "product",
    label: "Proizvod",
    hint: "Kroj, boja, uzorak, detalji, tekstura",
    positive:
      "PRESERVE THE GARMENT EXACTLY AS SHOWN IN THE REFERENCE: identical cut, " +
      "silhouette, length, neckline, sleeves and the way the fabric drapes; " +
      "identical fabric colour and exact shade, with no colour grading, saturation " +
      "boost or beautification; identical pattern or print including its orientation, " +
      "density and scale; every functional detail — buttons, zippers, clasps, ties, " +
      "pockets, seams, lace and ruffles — kept in exactly the same number and position; " +
      "identical material texture, so satin stays glossy, cotton stays matte and knit " +
      "stays visibly knitted.",
  },
  {
    id: "body",
    label: "Tijelo i poza",
    hint: "Proporcije, poza, lice, ten, frizura",
    positive:
      "Keep the model's body proportions and pose exactly as in the reference; do not " +
      "restyle the posture. Preserve the person's face, skin tone and hairstyle.",
  },
  {
    id: "sceneOnly",
    label: "Mijenjaj samo scenu",
    hint: "Ne diraj odjeću ni osobu",
    positive:
      "Change only the background, lighting and scene styling. Do not alter the clothing " +
      "or the person in any way, and do not let the scene's style bleed into the fabric.",
  },
  {
    id: "forbid",
    label: "Zabrane",
    hint: "Bez novih elemenata i deformacija",
    negative:
      "added jewelry, added belt, added appliques, extra garment elements, changed dress " +
      "length, changed neckline, altered sleeve length, deformed hands, deformed fingers, " +
      "extra fingers, missing buttons, warped print",
    negativeFallback:
      "DO NOT add any element that is not present on the original garment, such as " +
      "jewellery, belts or appliqués. DO NOT change the dress length or the neckline. " +
      "DO NOT deform the hands or fingers where they touch the fabric.",
  },
  {
    id: "technical",
    label: "Tehnički",
    hint: "Oštrina na zumu, usklađeno osvjetljenje",
    positive:
      "Keep fine detail sharp and legible when zoomed in, suitable for an e-commerce " +
      "product gallery. Match the lighting and shadows on the garment to the new scene " +
      "so it does not look pasted in.",
  },
];

export const DEFAULT_GUARDS = ["product", "forbid", "technical"];

export const GUARDS_STORAGE_KEY = "lunatik-guards";

export function loadGuards(): string[] {
  try {
    const raw = localStorage.getItem(GUARDS_STORAGE_KEY);
    if (!raw) return DEFAULT_GUARDS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_GUARDS;
    return parsed.filter((id) => GUARD_GROUPS.some((g) => g.id === id));
  } catch {
    return DEFAULT_GUARDS;
  }
}

/**
 * Expands the user's prompt with the constraint sentences for every active
 * guard. Forbidden-element rules go to the negative prompt when the model has
 * one, and are folded into the prompt itself otherwise.
 */
export function buildGuardedPrompt(
  prompt: string,
  activeIds: string[],
  supportsNegative: boolean
): { finalPrompt: string; negativeAdditions: string } {
  const active = GUARD_GROUPS.filter((g) => activeIds.includes(g.id));

  const parts: string[] = [];
  const base = prompt.trim();
  if (base) parts.push(base);

  const negatives: string[] = [];

  for (const group of active) {
    if (group.positive) parts.push(group.positive);
    if (group.negative || group.negativeFallback) {
      if (supportsNegative && group.negative) {
        negatives.push(group.negative);
      } else if (group.negativeFallback) {
        parts.push(group.negativeFallback);
      }
    }
  }

  return {
    finalPrompt: parts.join(" "),
    negativeAdditions: negatives.join(", "),
  };
}
